import { Experience } from '../';
import { ajaxService } from '../../common/Services';
import { NGXAjax } from '../../common/Services/AjaxService';
import { ApiUtils } from '../../common/api';

function fetchExperience(experienceId) {
  return ajaxService
    .get('/api/xp/campaign/show.json', {
      id: experienceId,
    })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1)
    .then(Experience.Model.deserialize);
}

function fetchExperiences(
  status,
  sort = 'lastUpdated',
  order = 'desc',
  searchFilter,
  offset = 0,
  max = 50,
  includeSubaccountsFilter
) {
  return ajaxService
    .get('/api/xp/campaign/list.json', {
      status,
      sort,
      order,
      search_filter:             searchFilter,
      offset,
      max,
      brief:                     true,
      includeSubaccounts_filter: includeSubaccountsFilter,
    })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extractWithMeta(['count']))
    .then(Experience.Collection.deserialize);
}

function fetchExperienceMetrics(includeSubaccounts) {
  return ajaxService
    .get('/api/xp/campaign/summary.json', { includeSubaccounts })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1)
    .catch((error) => ({ error }));
}

// TODO: get this updated to xapi
function cloneExperience(cloneParams) {
  return ajaxService
    .post('/api/campaign/clone', cloneParams, {}, { timeout: 0 }) //we are allowing customers to clone an experience to several destination(child) accounts, so need to allow for more time
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1)
    .catch((error) => ({ error }));
}

// experience status and status dates (publishDate, expiryDate, retentionDate) should not be sent during the common experience update
// this approach solves the invalid status issue when saving the experience from the different tabs or by different users at the same time
function _omitReservedProps(payload) {
  return _.omit(payload, ['status', 'publishDate', 'retentionDate', 'expiryDate', 'approval']);
}

function _updateSerializedExperience(serializedPayload, additionalPayload) {
  const { id, ...rest } = serializedPayload;

  return ajaxService
    .post(
      `/api/xp/campaign/update/${id}.json`,
      // I don't want to touch this as it was recently put in place for WYN-5619 but...
      // this will only ever successfully apply NEW attributes that don't exist in `...rest`, right?
      // if you provide params that already exist on the experience, rest will overwrite them. was that the intention?
      _omitReservedProps({
        ...rest,
        ...additionalPayload,
      }),
      undefined,
      { postJSON: true }
    )
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1)
    .then(Experience.Model.deserialize)
    .catch((error) => ({ error }));
}

function updateExperience(experience, additionalPayload) {
  return _updateSerializedExperience(Experience.Model.serialize(experience), additionalPayload);
}

function validateExternalRef(id, experienceMetadata, typeRef, subtypeRef) {
  return ajaxService
    .post('/api/xp/campaign/checkExternalRef.json', {
      campaignId: id,
      experienceMetadata,
      typeRef,
      subtypeRef,
    })
    .then(ApiUtils.validate, ApiUtils.validate);
}

function updateTheme(experience) {
  const { id, theme } = Experience.Model.serialize(experience);
  return _updateSerializedExperience({
    id,
    theme,
  });
}

function updateExperienceBackground(experience) {
  const { id, backgroundAssetRef } = Experience.Model.serialize(experience);
  return _updateSerializedExperience({
    id,
    backgroundAssetRef,
  });
}

function updateExperienceHeader(experience) {
  const { id, headerConfig } = Experience.Model.serialize(experience);
  return _updateSerializedExperience({
    id,
    headerConfig,
  });
}

function updatePolicyConfig(experience) {
  const { id, policyConfig, rulesText, rulesURL } = Experience.Model.serialize(experience);
  return _updateSerializedExperience({
    id,
    policyConfig,
    rulesText,
    rulesURL,
  });
}

function approve(id) {
  return ajaxService
    .post('/api/xp/campaign/approve.json', { id })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1)
    .then(Experience.Model.deserialize);
}

function unapprove(id) {
  return ajaxService
    .post('/api/xp/campaign/unapprove.json', { id })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1)
    .then(Experience.Model.deserialize);
}

function deleteExperience(id) {
  return ajaxService
    .post('/api/xp/campaign/delete.json', { id })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1);
}

function resetExperienceData(id) {
  return ajaxService
    .post('/api/xp/campaign/resetStats.json', { id })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1)
    .then(Experience.Model.deserialize);
}

function publish(
  id,
  {
    appName,
    containerId,
    channelId,
    displayUnitId,
    containerType,
    publishDate,
    expiryDate,
    ratio,
    width,
    height,
    privacyMgmt,
    displayConfig,
    privacyConfig,
  }
) {
  return ajaxService
    .post('/api/xp/campaign/publish.json', {
      id,
      appName,
      containerId,
      channelId,
      duId:          displayUnitId,
      containerType: containerType,
      publishDate:   publishDate === null ? undefined : publishDate,
      expiryDate:    expiryDate === null ? undefined : expiryDate,
      width,
      height,
      ratio,
      privacyMgmt,
      displayConfig,
      privacyConfig,
    })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1)
    .then((data) => ({
      experience:            Experience.Model.deserialize(data.campaignObject),
      // Intentionally not deserlializing publishingDestination here because don't have access to the entity.
      publishingDestination: data.publishingDestination,
    }))
    .catch((error) => ({ error }));
}

function republish(id, params = {}) {
  const { publishDate, expiryDate } = params;
  return ajaxService
    .post('/api/xp/campaign/republish.json', {
      id,
      publishDate: publishDate === null ? undefined : publishDate,
      expiryDate:  expiryDate === null ? undefined : expiryDate,
    })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1)
    .then(Experience.Model.deserialize)
    .catch((error) => ({ error }));
}

function publishNow(id, expiryDate) {
  return ajaxService
    .post('/api/xp/campaign/publishNow.json', { id, expiryDate: expiryDate || undefined })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1)
    .then(Experience.Model.deserialize)
    .catch((error) => ({ error }));
}

function scheduleExpiration(id, expiryDate) {
  return ajaxService
    .post('/api/xp/campaign/reschedule', { id, expiryDate: expiryDate || undefined })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1)
    .then(Experience.Model.deserialize)
    .catch((error) => ({ error }));
}

function expire(id) {
  return ajaxService
    .post('/api/xp/campaign/expireNow', { id })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1)
    .then(Experience.Model.deserialize)
    .catch((error) => ({ error }));
}

function unschedule(id) {
  return ajaxService
    .post('/api/xp/campaign/unschedule.json', { id })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1)
    .then(Experience.Model.deserialize)
    .catch((error) => ({ error }));
}

function reschedule(id, publishDate, expiryDate) {
  return ajaxService
    .post('/api/xp/campaign/reschedule.json', {
      id,
      publishDate,
      expiryDate: expiryDate === null ? undefined : expiryDate,
    })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1)
    .then(Experience.Model.deserialize)
    .catch((error) => ({ error }));
}

function fetchExperienceQuotaStatus() {
  return ajaxService
    .get('/api/campaign/statusActiveCampaignsQuota.json')
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract)
    .catch((error) => ({ error }));
}

export default {
  approve,
  deleteExperience,
  expire,
  fetchExperience,
  fetchExperiences,
  fetchExperienceMetrics,
  cloneExperience,
  publish,
  publishNow,
  republish,
  reschedule,
  resetExperienceData,
  scheduleExpiration,
  unapprove,
  unschedule,
  updateExperience,
  updateExperienceHeader,
  updateExperienceBackground,
  updatePolicyConfig,
  updateTheme,
  validateExternalRef,
  fetchExperienceQuotaStatus,
};
